import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Swal from "sweetalert2";

import * as S from "../css/AddBarcode.styles";
import MenuComponent from "../component/MenuComponent";
import Loading from "../component/Loading";
import { loginState } from "../stores/loginState";
import { barcodeAddState } from "../stores/addbarcode/barcodeAddState";
import { selectBarcodeState } from "../stores/addbarcode/selectBarcodeState";
import { useGetStatus } from "../api/addbarcode/useGetStatus";
import { useAddBarcode } from "../api/addbarcode/useAddBarcode";
import { useUploadManualScanFile } from "../api/addbarcode/useUploadManualScanFile";
import { useGetManualScanFileHistory } from "../api/addbarcode/useGetManualScanFileHistory";
import { formatNumberCommas } from "../utils/formatNumberCommas";

const BARCODE_PATTERN = /^\d{11,13}$/;
const TAB_REGISTER = "register";
const TAB_HISTORY = "history";

function AddBarcode(props) {
  const login = useRecoilValue(loginState);
  const [selectedData, setSelectedData] = useRecoilState(selectBarcodeState);
  const addData = useRecoilValue(barcodeAddState);

  const [isLoading, setIsLoading] = useState(false);
  const [lineCount, setLineCount] = useState(0);
  const [warningMessage, setWarningMessage] = useState("");
  const [activeTab, setActiveTab] = useState(TAB_REGISTER);
  const [selectedFile, setSelectedFile] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyReloadKey, setHistoryReloadKey] = useState(0);

  const fileInputRef = useRef(null);

  const parseBarcodeLines = (value) =>
    value
      .split(/\r?\n/)
      .map((barcode) => barcode.trim())
      .filter((barcode) => barcode !== "");

  const hasInvalidBarcode = (barcodeList) =>
    barcodeList.some((barcode) => !BARCODE_PATTERN.test(barcode));

  const syncBarcodeValue = (value) => {
    const barcodeList = parseBarcodeLines(value);

    setSelectedData((prevState) => ({
      ...prevState,
      barcode: value,
    }));

    setLineCount(barcodeList.length);

    if (barcodeList.length === 0) {
      setWarningMessage("");
      return;
    }

    setWarningMessage(
      hasInvalidBarcode(barcodeList)
        ? "송장번호는 한 줄에 하나씩 11~13자리 숫자만 입력해야 합니다."
        : "",
    );
  };

  const { mutate: getStatus } = useGetStatus(selectedData.date);
  const { mutateAsync: addBarcode } = useAddBarcode(setIsLoading);
  const { mutateAsync: uploadManualScanFile } = useUploadManualScanFile();
  const { mutateAsync: getManualScanFileHistory } =
    useGetManualScanFileHistory();

  useEffect(() => {
    getStatus();
  }, [selectedData.date, getStatus]);

  useEffect(() => {
    if (selectedData.date === "") {
      setSelectedData((prev) => ({
        ...prev,
        date: dayjs().format("YYYY-MM-DD"),
      }));
    }

    if (selectedData.tmdv === "") {
      setSelectedData((prev) => ({
        ...prev,
        tmdv: login.company === "LOGEN" ? "60" : "21",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== TAB_HISTORY) {
      return;
    }

    if (!login.branchCode) {
      setHistoryList([]);
      setHistoryError("지점 코드가 없어 조회할 수 없습니다.");
      return;
    }

    let isMounted = true;

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError("");

      try {
        const response = await getManualScanFileHistory(login.branchCode);
        if (!isMounted) {
          return;
        }

        const payload = response?.data ?? response;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        setHistoryList(rows);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error(
          "수동 등록 파일 히스토리 조회 중 오류가 발생했습니다.",
          error,
        );
        setHistoryList([]);
        setHistoryError(
          "히스토리를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
        );
      } finally {
        if (isMounted) {
          setHistoryLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [activeTab, getManualScanFileHistory, historyReloadKey, login.branchCode]);

  const handleTextareaChange = (e) => {
    const value = e.target.value.replace(/-/g, "").replace(/[^\d\n]/g, "");

    if (selectedFile) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }

    syncBarcodeValue(value);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const isTxtFile =
      file.name.toLowerCase().endsWith(".txt") ||
      file.type === "text/plain" ||
      file.type === "";

    if (!isTxtFile) {
      Swal.fire({
        icon: "warning",
        title: "txt 파일만 업로드할 수 있습니다.",
        confirmButtonText: "확인",
      });
      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const text = String(reader.result ?? "");
      const barcodeList = parseBarcodeLines(text.replace(/-/g, ""));

      if (barcodeList.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "유효한 송장번호가 없습니다.",
          text: "송장번호는 11~13자리 숫자만 한 줄에 하나씩 입력되어야 합니다.",
          confirmButtonText: "확인",
        });
        e.target.value = "";
        return;
      }

      if (hasInvalidBarcode(barcodeList)) {
        Swal.fire({
          icon: "warning",
          title: "파일 형식이 올바르지 않습니다.",
          text: "txt 파일 안에는 11~13자리 숫자만 한 줄에 하나씩 있어야 합니다.",
          confirmButtonText: "확인",
        });
        e.target.value = "";
        return;
      }

      syncBarcodeValue(barcodeList.join("\n"));
      setSelectedFile(file);
      e.target.value = "";
    };

    reader.onerror = () => {
      Swal.fire({
        icon: "error",
        title: "파일을 읽는 중 문제가 발생했습니다.",
        confirmButtonText: "확인",
      });
      e.target.value = "";
    };

    reader.readAsText(file, "utf-8");
  };

  const handleRegister = async () => {
    const barcodeList = parseBarcodeLines(selectedData.barcode);

    if (barcodeList.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "송장번호가 없습니다.",
        text: "11~13자리 숫자를 한 줄에 하나씩 입력해 주세요.",
        confirmButtonText: "확인",
      });
      return;
    }

    if (hasInvalidBarcode(barcodeList)) {
      Swal.fire({
        icon: "warning",
        title: "송장번호 형식이 올바르지 않습니다.",
        text: "송장번호는 11~13자리 숫자만 한 줄에 하나씩 입력해야 합니다.",
        confirmButtonText: "확인",
      });
      return;
    }

    setIsLoading(true);

    try {
      const startTime = dayjs().format("YYYY-MM-DD 06:00:00");

      const dataList = barcodeList.map((barcode, index) => {
        const currentTime = dayjs(startTime)
          .add(index, "second")
          .format("YYYY-MM-DD HH:mm:ss");

        return {
          emp_cd: selectedData.empcd,
          tg_bran_cd: selectedData.tgbran,
          pob: selectedData.pob,
          car_num: selectedData.car,
          scandate: selectedData.date,
          tm_dv: selectedData.tmdv,
          bran_cd: login.branchCode,
          dev_serial: "watcher",
          dev_ver: "",
          barcode,
          scan_total_time: currentTime,
          scantime: dayjs(currentTime).format("HH:mm:ss"),
        };
      });

      await addBarcode(dataList);

      if (selectedFile) {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("bran_cd", login.branchCode);
        formData.append("scan_count", String(barcodeList.length));

        const createdBy = login.userId || login.accountId || login.saId || "";
        if (createdBy) {
          formData.append("created_by", createdBy);
        }

        formData.append("file", selectedFile);

        try {
          await uploadManualScanFile(formData);
        } catch (uploadError) {
          console.error("파일 업로드 중 오류가 발생했습니다.", uploadError);
          Swal.fire({
            icon: "warning",
            title: "송장번호는 등록되었지만 파일 업로드는 실패했습니다.",
            text: "파일만 다시 업로드해 주세요.",
            confirmButtonText: "확인",
          });
        }
      }
    } catch (error) {
      console.error("등록 처리 중 오류가 발생했습니다.", error);
      Swal.fire({
        icon: "warning",
        title: "등록 처리 중 문제가 발생했습니다.",
        confirmButtonText: "확인",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleHistoryRefresh = () => {
    setHistoryReloadKey((prev) => prev + 1);
  };

  const renderRegisterTab = () => (
    <S.FlexContainer>
      <S.EditContainer style={{ width: "420px", flex: "0 0 420px" }}>
        <S.EditSpan>
          <h5>운행일자</h5>
          <S.Input
            type="date"
            value={selectedData.date}
            onChange={(e) =>
              setSelectedData((prevState) => ({
                ...prevState,
                date: e.target.value,
              }))
            }
          />
        </S.EditSpan>
        <S.EditSpan>
          <h5>운행구분</h5>
          <S.SelectContainer
            value={selectedData.tmdv}
            onChange={(e) =>
              setSelectedData((prevState) => ({
                ...prevState,
                tmdv: e.target.value,
              }))
            }
          >
            <option disabled>선택해 주세요.</option>
            <option value="60">배송입고 (60)</option>
            <option value="30">집하출고 (30)</option>
          </S.SelectContainer>
        </S.EditSpan>
        <S.EditSpan>
          <h5>작업영업점</h5>
          <S.Input
            value={login.branchCode}
            readOnly
            style={{ background: props.theme.white2 }}
          />
        </S.EditSpan>
        <S.EditSpan>
          <h5>작업영업사원</h5>
          <Autocomplete
            value={selectedData.empcd}
            disablePortal
            id="add-barcode-emp-code"
            options={addData.emp_cd || []}
            filterOptions={(options) => options}
            sx={{ width: 150, height: 28 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="입력하거나 선택해 주세요."
                variant="outlined"
              />
            )}
            onChange={(event, newValue) => {
              setSelectedData((prevState) => ({
                ...prevState,
                empcd: newValue || "",
              }));
            }}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== selectedData.empcd) {
                setSelectedData((prevState) => ({
                  ...prevState,
                  empcd: newInputValue || "",
                }));
              }
            }}
          />
        </S.EditSpan>
        <S.EditSpan>
          <h5>출발영업점</h5>
          <Autocomplete
            value={selectedData.tgbran}
            disablePortal
            id="add-barcode-target-branch"
            options={addData.tg_bran_cd || []}
            filterOptions={(options) => options}
            sx={{ width: 150, height: 28 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="입력하거나 선택해 주세요."
                variant="outlined"
              />
            )}
            onChange={(event, newValue) => {
              setSelectedData((prevState) => ({
                ...prevState,
                tgbran: newValue || "",
              }));
            }}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== selectedData.tgbran) {
                setSelectedData((prevState) => ({
                  ...prevState,
                  tgbran: newInputValue || "",
                }));
              }
            }}
          />
        </S.EditSpan>
        <S.EditSpan>
          <h5>출발영업사원</h5>
          <Autocomplete
            value={selectedData.pob}
            disablePortal
            id="add-barcode-target-employee"
            options={addData.pob || []}
            filterOptions={(options) => options}
            sx={{ width: 150, height: 28 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="입력하거나 선택해 주세요."
                variant="outlined"
              />
            )}
            onChange={(event, newValue) => {
              setSelectedData((prevState) => ({
                ...prevState,
                pob: newValue || "",
              }));
            }}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== selectedData.pob) {
                setSelectedData((prevState) => ({
                  ...prevState,
                  pob: newInputValue || "",
                }));
              }
            }}
          />
        </S.EditSpan>
        <S.EditSpan>
          <h5>차량번호</h5>
          <Autocomplete
            value={selectedData.car}
            disablePortal
            id="add-barcode-car-number"
            options={addData.car_num || []}
            filterOptions={(options) => options}
            sx={{ width: 150, height: 28 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="입력하거나 선택해 주세요."
                variant="outlined"
              />
            )}
            onChange={(event, newValue) => {
              setSelectedData((prevState) => ({
                ...prevState,
                car: newValue || "",
              }));
            }}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== selectedData.car) {
                setSelectedData((prevState) => ({
                  ...prevState,
                  car: newInputValue || "",
                }));
              }
            }}
          />
        </S.EditSpan>
        <hr />
        <h6>
          운행일자를 바꾸면 해당 일자의 영업점 정보가 자동으로 업데이트됩니다.
          <br />
          모든 정보를 입력해야 등록이 가능합니다.
          <br />
          차량번호는 선택 입력과 직접 입력 모두 지원합니다.
        </h6>
        <hr />
        <h6>
          메모장이나 엑셀에서 송장번호를 세로로 복사해서 그대로 붙여 넣거나,
          <br />
          같은 형식의 txt(엑셀 불가) 파일을 업로드해도 동일하게 등록할 수
          있습니다.
          <br />
          <br />
          송장번호는 한 줄에 하나씩 11~13자리 숫자만 허용됩니다.
        </h6>
      </S.EditContainer>
      <S.EditContainer style={{ width: "420px", flex: "0 0 420px" }}>
        <h5>운송장번호 등록</h5>
        <S.TextArea
          value={selectedData.barcode}
          onInput={(e) => {
            e.target.value = e.target.value.replace(/[^\d\n]/g, "");
          }}
          placeholder="송장번호를 입력해 주세요."
          onChange={handleTextareaChange}
        />
        {warningMessage && (
          <p style={{ color: "red" }}>
            {warningMessage.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
        )}
        <p>검출된 운송장 {formatNumberCommas(lineCount)} 건</p>

        <S.ActionBar>
          <S.ActionButton type="button" onClick={handleUploadClick}>
            업로드
          </S.ActionButton>
          <S.ActionButton
            type="button"
            $variant="primary"
            onClick={handleRegister}
          >
            등록
          </S.ActionButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,text/plain"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </S.ActionBar>

        <S.FileMeta>
          <div>첨부 파일: {selectedFile ? selectedFile.name : "없음"}</div>
        </S.FileMeta>
      </S.EditContainer>
    </S.FlexContainer>
  );

  const renderHistoryTab = () => (
    <S.EditContainer
      style={{ minWidth: "100%", width: "100%", flex: "1 1 100%" }}
    >
      <S.HistoryPanel>
        <S.HistoryTop>
          <div>
            <h5>추가 등록 히스토리</h5>
            <S.HistoryHint>
              현재 지점 코드: {login.branchCode || "-"}
            </S.HistoryHint>
          </div>
          <S.ActionButton type="button" onClick={handleHistoryRefresh}>
            새로고침
          </S.ActionButton>
        </S.HistoryTop>

        {historyLoading ? (
          <div style={{ minHeight: "220px", position: "relative" }}>
            <Loading />
          </div>
        ) : historyError ? (
          <S.EmptyState>{historyError}</S.EmptyState>
        ) : historyList.length === 0 ? (
          <S.EmptyState>조회된 히스토리가 없습니다.</S.EmptyState>
        ) : (
          <S.HistoryTableWrap>
            <S.HistoryTable>
              <thead>
                <tr>
                  <S.HistoryTh>번호</S.HistoryTh>
                  <S.HistoryTh>지점코드</S.HistoryTh>
                  <S.HistoryTh>파일명</S.HistoryTh>
                  <S.HistoryTh>등록자</S.HistoryTh>
                  <S.HistoryTh>등록일시</S.HistoryTh>
                  <S.HistoryTh>파일크기</S.HistoryTh>
                  <S.HistoryTh>건수</S.HistoryTh>
                  <S.HistoryTh>다운로드</S.HistoryTh>
                </tr>
              </thead>
              <tbody>
                {historyList.map((item, index) => (
                  <tr key={item.id}>
                    <S.HistoryTd>{index + 1}</S.HistoryTd>
                    <S.HistoryTd>{item.bran_cd || "-"}</S.HistoryTd>
                    <S.HistoryTd>{item.original_file_name || "-"}</S.HistoryTd>
                    <S.HistoryTd>{item.created_by || "-"}</S.HistoryTd>
                    <S.HistoryTd>
                      {item.created_at
                        ? dayjs(item.created_at).format("YYYY-MM-DD HH:mm:ss")
                        : "-"}
                    </S.HistoryTd>
                    <S.HistoryTd>
                      {item.file_size
                        ? `${formatNumberCommas(item.file_size)} B`
                        : "-"}
                    </S.HistoryTd>
                    <S.HistoryTd>{item.scan_count ?? "-"}</S.HistoryTd>
                    <S.HistoryTd>
                      {item.file_url ? (
                        <a
                          href={item.file_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          다운로드
                        </a>
                      ) : (
                        "-"
                      )}
                    </S.HistoryTd>
                  </tr>
                ))}
              </tbody>
            </S.HistoryTable>
          </S.HistoryTableWrap>
        )}
      </S.HistoryPanel>
    </S.EditContainer>
  );

  return (
    <S.DashboardContainer>
      <MenuComponent theme={props.theme} />
      <S.ContentContainer>
        {isLoading && <Loading />}
        <h4>운송장 추가등록</h4>

        <S.TabBar>
          <S.TabButton
            type="button"
            $active={activeTab === TAB_REGISTER}
            onClick={() => setActiveTab(TAB_REGISTER)}
          >
            등록
          </S.TabButton>
          <S.TabButton
            type="button"
            $active={activeTab === TAB_HISTORY}
            onClick={() => setActiveTab(TAB_HISTORY)}
          >
            조회
          </S.TabButton>
        </S.TabBar>

        {activeTab === TAB_REGISTER ? renderRegisterTab() : renderHistoryTab()}
      </S.ContentContainer>
    </S.DashboardContainer>
  );
}

export default AddBarcode;
